import React, { useEffect, useRef, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { CheckCircleIcon as CheckCircle, SpinnerIcon as Spinner, WarningIcon as Warning, ArrowRightIcon as ArrowRight, BankIcon as Bank, ArrowClockwiseIcon as ArrowClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";

/**
 * Stripe Connect onboarding return page.
 *
 * Every call to POST /api/connect/account should pass this page as
 * `return_url` (with an optional `?flow=` hint). After the user
 * finishes (or bails out of) Stripe's hosted onboarding, Stripe
 * redirects here.
 *
 * The polling window is the whole point: right after a user finishes
 * Express onboarding, Stripe&rsquo;s API can take 5-60 seconds to
 * report `payouts_enabled: true`. Without this page, the user lands
 * directly back on their dashboard, sees "Finish payouts setup"
 * still pinned, and either rage-clicks again or files a support
 * ticket. With this page, we poll the canonical Stripe status every
 * 3s for up to 60s before showing a definitive answer.
 *
 * Mobile considerations baked in:
 *   * Single-column layout, no horizontal scroll at 375px.
 *   * Large primary CTA (h-12, full-width on mobile).
 *   * URL is bookmarkable + survives a refresh — the polling resumes
 *     from wherever the user lands.
 *   * `?flow=` query param drives the post-success CTA destination so
 *     each persona returns to the right contextual dashboard.
 *   * `?status=refresh` is what Stripe sends when the user bails out
 *     mid-onboarding (link expired or cancel). We treat that as
 *     "resume" instead of a terminal failure.
 */
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 60_000;

const FLOW_CONFIG = {
  breeder: {
    label: "Breeder dashboard",
    success_cta_label: "Go to my kennel dashboard",
    success_cta_to: "/dashboard",
    success_followup:
      "Your next dog sale will flow through escrow and pay out to your bank automatically.",
  },
  storefront: {
    label: "Storefront dashboard",
    success_cta_label: "Open my storefront",
    success_cta_to: "/me/store",
    success_followup:
      "Customers can now check out from your storefront and the funds will pay out to your bank automatically.",
  },
  provider: {
    label: "Provider dashboard",
    success_cta_label: "Open my provider profile",
    success_cta_to: "/provider/me",
    success_followup:
      "Your next booking will be paid through Stripe Connect and pay out to your bank automatically.",
  },
  transporter: {
    label: "Transporter dashboard",
    success_cta_label: "Open my transporter dashboard",
    success_cta_to: "/transport/dashboard",
    success_followup:
      "Your next completed trip will pay out to your bank automatically.",
  },
  default: {
    label: "Dashboard",
    success_cta_label: "Back to dashboard",
    success_cta_to: "/dashboard",
    success_followup:
      "Payouts on Armstrong K9 Registry are now linked to your bank.",
  },
};

export default function StripeConnectReturn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const flow = (searchParams.get("flow") || "default").toLowerCase();
  const initialStatus = (searchParams.get("status") || "").toLowerCase();
  const cfg = FLOW_CONFIG[flow] || FLOW_CONFIG.default;

  // States: "polling" | "ready" | "pending_review" | "more_info" | "resume_onboarding" | "error"
  const [state, setState] = useState(initialStatus === "refresh" ? "resume_onboarding" : "polling");
  const [pollCount, setPollCount] = useState(0);
  const [details, setDetails] = useState(null);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);
  const startedAtRef = useRef(Date.now());

  // Poll loop — runs only in the "polling" state. Clears itself on
  // any terminal state OR after POLL_TIMEOUT_MS, whichever comes first.
  useEffect(() => {
    if (state !== "polling") return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.post("/connect/account/refresh-status");
        if (cancelled) return;
        setDetails(data);
        setPollCount((c) => c + 1);
        const next = data?.next_action;
        if (next === "ready") {
          setState("ready");
          return;
        }
        if (next === "more_info") {
          setState("more_info");
          return;
        }
        if (next === "pending_review") {
          // Keep polling — Stripe may flip ready in a few seconds.
          if (Date.now() - startedAtRef.current >= POLL_TIMEOUT_MS) {
            setState("pending_review");
            return;
          }
        }
        if (next === "resume_onboarding" || next === "start") {
          setState("resume_onboarding");
          return;
        }
        if (Date.now() - startedAtRef.current >= POLL_TIMEOUT_MS) {
          // Timed out without a definitive answer — show the
          // pending-review screen so the user has somewhere to go.
          setState("pending_review");
        }
      } catch (err) {
        if (cancelled) return;
        setState("error");
      }
    };
    // Fire immediately so the user doesn't stare at a spinner before
    // the first poll. Then on the regular interval.
    tick();
    timerRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [state]);

  const resumeOnboarding = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/connect/account", {
        return_url: `${webOrigin()}/connect/return?flow=${flow}`,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Stripe onboarding link unavailable.");
      }
    } catch (err) {
      setBusy(false);
      setState("error");
      setDetails((d) => ({ ...(d || {}), error_message: formatApiError(err) }));
    }
  };

  // -----------------------------------------------------------------
  // Sub-renders
  // -----------------------------------------------------------------
  const Container = ({ children }) => (
    <div className="max-w-md mx-auto" data-testid={`connect-return-${state}`}>
      <div className="rounded-2xl border border-[#EAEAEA] bg-white p-6 sm:p-8">{children}</div>
    </div>
  );

  if (state === "polling") {
    const elapsed = Math.round((Date.now() - startedAtRef.current) / 1000);
    return (
      <AppShell>
        <Container>
          <div className="flex flex-col items-center text-center">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FBF7EC] border border-gold/40 mb-4">
              <Spinner size={28} weight="bold" className="text-gold-dark animate-spin" />
            </span>
            <h1 className="font-spectral text-2xl text-ink leading-tight">
              Checking with Stripe…
            </h1>
            <p className="text-[13.5px] text-[#6B6B6B] mt-2 leading-relaxed">
              Stripe usually clears new Connect accounts in under a minute.
              We&rsquo;re polling for confirmation now — this page will
              update automatically.
            </p>
            <p className="text-[11px] text-[#9A9A9A] mt-4 font-tabular">
              Elapsed: {elapsed}s · checks: {pollCount}
            </p>
          </div>
        </Container>
      </AppShell>
    );
  }

  if (state === "ready") {
    return (
      <AppShell>
        <Container>
          <div className="flex flex-col items-center text-center">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 mb-4">
              <CheckCircle size={32} weight="fill" className="text-emerald-600" />
            </span>
            <h1 className="font-spectral text-2xl text-ink leading-tight">
              Payouts are live.
            </h1>
            <p className="text-[14px] text-[#3F3F3F] mt-2 leading-relaxed">
              Stripe has verified your Connect account.{" "}
              {cfg.success_followup}
            </p>
            <Button
              onClick={() => navigate(cfg.success_cta_to)}
              className="mt-6 w-full sm:w-auto h-12 px-6 rounded-full bg-ink hover:bg-crimson text-white font-bold"
              data-testid="connect-return-ready-cta"
            >
              {cfg.success_cta_label}
              <ArrowRight size={14} weight="bold" className="ml-1.5" />
            </Button>
          </div>
        </Container>
      </AppShell>
    );
  }

  if (state === "pending_review") {
    return (
      <AppShell>
        <Container>
          <div className="flex flex-col items-center text-center">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-50 border border-amber-200 mb-4">
              <Bank size={28} weight="duotone" className="text-amber-700" />
            </span>
            <h1 className="font-spectral text-2xl text-ink leading-tight">
              Stripe is still reviewing.
            </h1>
            <p className="text-[14px] text-[#3F3F3F] mt-2 leading-relaxed">
              You finished onboarding but Stripe hasn&rsquo;t flipped your
              payouts on yet — that&rsquo;s normal and usually clears in
              under 24 hours. We&rsquo;ll send you an email the moment it
              does. You can keep using the rest of the platform in the
              meantime.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setState("polling");
                startedAtRef.current = Date.now();
              }}
              className="mt-6 w-full sm:w-auto h-11 rounded-full"
              data-testid="connect-return-recheck-cta"
            >
              <ArrowClockwise size={14} weight="bold" className="mr-1.5" />
              Check again now
            </Button>
            <Link
              to={cfg.success_cta_to}
              className="mt-3 text-[12.5px] font-semibold text-ink hover:text-crimson underline"
              data-testid="connect-return-pending-back"
            >
              Back to {cfg.label.toLowerCase()}
            </Link>
          </div>
        </Container>
      </AppShell>
    );
  }

  if (state === "more_info") {
    const due = details?.account?.requirements_currently_due || [];
    const dueLabels = details?.account?.requirements_currently_due_labels || due;
    return (
      <AppShell>
        <Container>
          <div className="flex flex-col items-center text-center">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-50 border border-amber-200 mb-4">
              <Warning size={28} weight="fill" className="text-amber-700" />
            </span>
            <h1 className="font-spectral text-2xl text-ink leading-tight">
              Stripe needs a bit more info.
            </h1>
            <p className="text-[14px] text-[#3F3F3F] mt-2 leading-relaxed">
              You&rsquo;re almost done — Stripe is asking for a few more
              details before they can enable payouts. Tap below to finish
              right where you left off.
            </p>
            {dueLabels.length > 0 && (
              <div className="mt-3 w-full text-left bg-[#FFF8E1] border border-amber-200 rounded-lg px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#8A4A00] mb-1">Still needed</p>
                <ul className="text-[12.5px] text-ink space-y-0.5 leading-snug list-disc pl-4">
                  {dueLabels.slice(0, 5).map((label, i) => (
                    <li key={`${label}-${i}`}>{label}</li>
                  ))}
                  {dueLabels.length > 5 && (
                    <li className="text-[#6B6B6B] italic">…and {dueLabels.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}
            <Button
              onClick={resumeOnboarding}
              disabled={busy}
              className="mt-6 w-full sm:w-auto h-12 px-6 rounded-full bg-ink hover:bg-crimson text-white font-bold"
              data-testid="connect-return-resume-cta"
            >
              {busy ? "Opening Stripe…" : "Finish at Stripe"}
              <ArrowRight size={14} weight="bold" className="ml-1.5" />
            </Button>
            <button
              type="button"
              onClick={async () => {
                try {
                  const r = await import("@/lib/api").then((m) => m.default.post("/connect/account/login-link"));
                  if (r?.data?.url) {
                    window.location.href = r.data.url;
                  }
                } catch {
                  window.open("https://dashboard.stripe.com", "_blank", "noreferrer");
                }
              }}
              data-testid="connect-return-stripe-direct"
              className="mt-3 text-[12px] text-[#6B6B6B] hover:text-ink underline cursor-pointer bg-transparent border-0 p-0"
            >
              Or sign in to Stripe directly ↗
            </button>
          </div>
        </Container>
      </AppShell>
    );
  }

  if (state === "resume_onboarding") {
    return (
      <AppShell>
        <Container>
          <div className="flex flex-col items-center text-center">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FBF7EC] border border-gold/40 mb-4">
              <Bank size={28} weight="duotone" className="text-gold-dark" />
            </span>
            <h1 className="font-spectral text-2xl text-ink leading-tight">
              Resume Stripe onboarding
            </h1>
            <p className="text-[14px] text-[#3F3F3F] mt-2 leading-relaxed">
              Looks like the previous Stripe session expired or you stepped
              away. Tap below to pick up where you left off — none of your
              earlier answers were lost.
            </p>
            <Button
              onClick={resumeOnboarding}
              disabled={busy}
              className="mt-6 w-full sm:w-auto h-12 px-6 rounded-full bg-ink hover:bg-crimson text-white font-bold"
              data-testid="connect-return-resume-cta"
            >
              {busy ? "Opening Stripe…" : "Continue setup"}
              <ArrowRight size={14} weight="bold" className="ml-1.5" />
            </Button>
            <Link
              to={cfg.success_cta_to}
              className="mt-3 text-[12.5px] font-semibold text-ink hover:text-crimson underline"
              data-testid="connect-return-resume-back"
            >
              I&rsquo;ll do it later
            </Link>
          </div>
        </Container>
      </AppShell>
    );
  }

  // error
  return (
    <AppShell>
      <Container>
        <div className="flex flex-col items-center text-center">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-crimson/10 border border-crimson/30 mb-4">
            <Warning size={28} weight="fill" className="text-crimson" />
          </span>
          <h1 className="font-spectral text-2xl text-ink leading-tight">
            We hit a snag.
          </h1>
          <p className="text-[14px] text-[#3F3F3F] mt-2 leading-relaxed">
            {details?.error_message || "Stripe didn't return a clean status. You can retry the check, or come back from your dashboard in a few minutes."}
          </p>
          <Button
            onClick={() => {
              setState("polling");
              startedAtRef.current = Date.now();
            }}
            className="mt-6 w-full sm:w-auto h-11 rounded-full"
            variant="outline"
            data-testid="connect-return-retry-cta"
          >
            <ArrowClockwise size={14} weight="bold" className="mr-1.5" />
            Try again
          </Button>
          <Link
            to={cfg.success_cta_to}
            className="mt-3 text-[12.5px] font-semibold text-ink hover:text-crimson underline"
            data-testid="connect-return-error-back"
          >
            Back to {cfg.label.toLowerCase()}
          </Link>
        </div>
      </Container>
    </AppShell>
  );
}
